import { useRef } from "react";
import { m, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ShieldCheck, Star } from "lucide-react";
import { AGENT, IMAGES } from "../data/site";

const lineUp = {
  hidden: { y: "110%" },
  show: (i) => ({ y: 0, transition: { duration: 0.9, delay: 0.15 * i, ease: [0.22, 1, 0.36, 1] } }),
};

const Line = ({ children, i }) => (
  <span className="block overflow-hidden">
    <m.span variants={lineUp} custom={i} initial="hidden" animate="show" className="block">
      {children}
    </m.span>
  </span>
);

export default function Hero({ onQuote }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);

  return (
    <section ref={ref} className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          <m.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="text-sm uppercase tracking-[0.25em] font-bold text-[#C85A40] mb-6"
            data-testid="hero-eyebrow"
          >
            {AGENT.role}
          </m.p>

          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-[0.95] text-[#1A1714]">
            <Line i={0}>Health coverage</Line>
            <Line i={1}>
              that finally <span className="italic text-[#C85A40]">feels</span>
            </Line>
            <Line i={2}>personal.</Line>
          </h1>

          <m.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="mt-8 max-w-xl text-base md:text-lg leading-relaxed text-[#4A453F]"
            data-testid="hero-subtitle"
          >
            Hi, I'm {AGENT.firstName}, an independent ACA health insurance agent who works for you, not the
            carriers. I turn confusing plans into clear choices, and I'm here long after you enroll.
          </m.p>

          <m.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.8 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-button"
              onClick={onQuote}
              className="inline-flex items-center rounded-full bg-[#C85A40] px-8 py-4 text-base font-semibold text-[#F9F6F0] hover:bg-[#A64730] active:scale-95 transition-[background-color,transform] duration-200"
            >
              Get my free quote
            </button>
          </m.div>

          <m.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.3 }}
            className="mt-10 flex items-center gap-6 text-sm text-[#4A453F]"
          >
            <span className="inline-flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-[#2C5E43]" /> $0 for my help
            </span>
            <span className="inline-flex items-center gap-2">
              <Star className="w-4 h-4 text-[#E8A365] fill-[#E8A365]" /> Licensed & independent
            </span>
          </m.div>
        </div>

        <div className="lg:col-span-5 relative">
          <m.div
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="arch overflow-hidden shadow-[0_24px_60px_rgba(26,23,20,0.18)]">
              <m.img
                style={{ y: imgY }}
                src={IMAGES.heroPortrait}
                srcSet="/images/jacqueline-portrait-640.webp 640w, /images/jacqueline-portrait-900.webp 900w, /images/jacqueline-portrait.webp?v=2 1125w"
                sizes="(min-width: 1024px) 40vw, 90vw"
                fetchPriority="high"
                alt={`${AGENT.name}, ${AGENT.role}`}
                data-testid="hero-portrait"
                className="w-full h-[420px] md:h-[560px] object-cover object-[50%_20%]"
              />
            </div>

            <m.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2, duration: 0.7 }}
              className="absolute -left-4 md:-left-10 bottom-10 bg-[#1E3A2F] text-[#F9F6F0] rounded-2xl px-6 py-5 shadow-[0_16px_40px_rgba(30,58,47,0.35)] max-w-[220px]"
              data-testid="hero-states-badge"
            >
              <p className="text-[11px] uppercase tracking-[0.2em] text-[#E8A365] font-bold">Where I'm licensed</p>
              <p className="mt-2 font-display text-3xl leading-none">Maryland</p>
            </m.div>
          </m.div>
        </div>
      </div>

      <m.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="max-w-7xl mx-auto px-5 sm:px-8 mt-16 flex items-center gap-3 text-[#4A453F]"
      >
        <ArrowDown className="w-4 h-4 animate-bounce" />
        <span className="text-xs uppercase tracking-[0.25em]">Scroll to explore</span>
      </m.div>
    </section>
  );
}
